/**
 * `wayai actions …` — reusable named operations a toolset composes by
 * reference. Top-level.
 */

import { Command } from 'commander';
import { expected } from '../../lib/expected.js';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildActionsCommand(): Command {
  const actions = new Command('actions').description(
    'Manage Actions — reusable, named definitions: one record_type + one operation, or a composite of atomic write steps (--config with `steps`)',
  );

  actions
    .command('upsert <id>')
    .description(
      'Create or update an Action — one record_type + one operation, or a composite (--config with `steps`)',
    )
    .option(
      '--record-type <record_type>',
      'The record_type this action operates over (single-op; omit for a composite)',
    )
    .option(
      '--operation <op>',
      'The operation: create | get | list | update | delete (single-op; omit for a composite)',
    )
    .option('--description <desc>', 'Agent-facing description')
    .option(
      '--config <json>',
      'Full Action config as JSON. Single-op: writable_fields, filterable_fields (per field: field (required), param, match, enum, pattern, optional, description — a declared field generates a required param unless optional: true; a range match is always optional), base_filter (a Filter DSL predicate merged server-side into every list call, invisible to the agent; list only, over a native record_type or a source-backed one whose list endpoint declares local_filters; the value "$now" resolves to the instant of each call, on a date-time field with a range op), precondition, binding, external_source, expose_*/default_* (machinery params are hidden unless expose_<param>: true). Composite: `steps` — an ordered list of atomic write steps ({key, record_type|rel_type, operation, writable_fields?, precondition?}) run in one transaction. Use @file.json to read from file.',
    )
    .action(async function (
      this: Command,
      id: string,
      opts: {
        recordType?: string;
        operation?: string;
        description?: string;
        config?: string;
      },
    ) {
      let body: Record<string, unknown>;
      if (opts.config) {
        body = parseData(opts.config);
        if (opts.recordType) body.record_type = opts.recordType;
        if (opts.operation) body.operation = opts.operation;
        if (opts.description) body.description = opts.description;
      } else {
        if (!opts.recordType || !opts.operation) {
          throw expected('Provide --record-type and --operation (or --config <json>).');
        }
        body = { record_type: opts.recordType, operation: opts.operation };
        if (opts.description) body.description = opts.description;
      }

      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('PUT', `/v1/${base}/actions/${pathSegment(id, 'action id')}`, body),
        outputFormat(this),
      );
    });

  actions
    .command('get <id>')
    .description('Get an Action by id')
    .action(async function (this: Command, id: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/actions/${pathSegment(id, 'action id')}`), outputFormat(this));
    });

  actions
    .command('list')
    .description('List Actions in this base')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/actions`), outputFormat(this));
    });

  actions
    .command('delete <id>')
    .description('Delete an Action')
    .option('-y, --yes', 'Skip confirmation')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      if (!opts.yes && !(await confirm(`Delete action ${id}? This cannot be undone.`))) {
        console.log('Aborted.');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/actions/${pathSegment(id, 'action id')}`);
      console.log(`Deleted action ${id}`);
    });

  return actions;
}
